// Leave — balance + request history.
import { useEffect, useState } from "react";
import { api } from "../../shared/api/client";
import { PageHeader } from "../../shared/components/PageHeader";
import { Empty, Loading } from "../../shared/components/Empty";
import { Pill } from "../../shared/components/Pill";
import { toast } from "../../shared/stores/toasts";

type Balance = { year: number; total_days: number; used_days: number; carried_over: number; remaining_days: number };
type Request = { id: number; leave_type: string; start_date: string; end_date: string; days: number; status: string; reason: string | null };

export default function LeavePage() {
  const [bal, setBal] = useState<Balance | null>(null);
  const [items, setItems] = useState<Request[] | null>(null);
  const [reqOpen, setReqOpen] = useState(false);

  const reload = async () => {
    const [b, r] = await Promise.all([api.get<Balance>("/leave/balance"), api.get<Request[]>("/leave/requests")]);
    setBal(b.data); setItems(r.data);
  };
  useEffect(() => { reload(); }, []);

  return (
    <>
      <PageHeader title="휴가" subtitle="연차 잔여 · 신청 이력"
        right={<button className="t-btn t-btn-primary" onClick={() => setReqOpen(true)}>휴가 신청</button>}
      />
      <div className="t-grid cols-4">
        <div className="t-card">
          <div style={{ fontSize: 11.5, color: "var(--text-3)" }}>총 부여</div>
          <div className="num" style={{ fontSize: 28, fontWeight: 700 }}>{bal?.total_days ?? "—"}일</div>
        </div>
        <div className="t-card">
          <div style={{ fontSize: 11.5, color: "var(--text-3)" }}>사용</div>
          <div className="num" style={{ fontSize: 28, fontWeight: 700 }}>{bal?.used_days ?? "—"}일</div>
        </div>
        <div className="t-card">
          <div style={{ fontSize: 11.5, color: "var(--text-3)" }}>이월</div>
          <div className="num" style={{ fontSize: 28, fontWeight: 700 }}>{bal?.carried_over ?? "—"}일</div>
        </div>
        <div className="t-card" style={{ background: "var(--p-tint)" }}>
          <div style={{ fontSize: 11.5, color: "var(--text-3)" }}>잔여</div>
          <div className="num" style={{ fontSize: 28, fontWeight: 700, color: "var(--p-700)" }}>{bal?.remaining_days ?? "—"}일</div>
        </div>
      </div>

      <div className="t-grid cols-1">
        {items === null && <Loading/>}
        {items?.length === 0 && <Empty msg="신청 이력이 없습니다"/>}
        <div className="t-card" style={{ padding: 0 }}>
          <div className="t-list" style={{ padding: 16 }}>
            {items?.map((r) => (
              <div key={r.id} className="t-row">
                <Pill kind="violet">{r.leave_type}</Pill>
                <span className="title" style={{ minWidth: 200 }}>{r.start_date} → {r.end_date}</span>
                <span className="meta">{r.days}일</span>
                <span className="meta" style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.reason}</span>
                <Pill kind={r.status === "APPROVED" ? "ok" : r.status === "REJECTED" ? "danger" : "warn"}>{r.status}</Pill>
              </div>
            ))}
          </div>
        </div>
      </div>

      {reqOpen && <LeaveRequestModal onClose={() => { setReqOpen(false); reload(); }} />}
    </>
  );
}

function LeaveRequestModal({ onClose }: { onClose: () => void }) {
  const [form, setForm] = useState({ leave_type: "ANNUAL", start_date: new Date().toISOString().slice(0, 10), end_date: new Date().toISOString().slice(0, 10), reason: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try { await api.post("/leave/requests", form); toast.success("휴가 신청 완료", "결재 문서가 자동 생성되었습니다"); onClose(); }
    catch (e: any) { toast.error("신청 실패", e?.response?.data?.error?.message); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="t-overlay" onClick={onClose}>
      <div className="t-cmdk" onClick={(e) => e.stopPropagation()} style={{ width: 480 }}>
        <div style={{ padding: 18, borderBottom: "1px solid var(--border)" }}><h3 style={{ margin: 0 }}>휴가 신청</h3></div>
        <div style={{ padding: 18, display: "grid", gap: 12 }}>
          <div>
            <label className="t-label">유형</label>
            <select className="t-input" value={form.leave_type} onChange={(e) => setForm({ ...form, leave_type: e.target.value })}>
              <option value="ANNUAL">연차</option>
              <option value="HALF_AM">오전 반차</option>
              <option value="HALF_PM">오후 반차</option>
              <option value="SICK">병가</option>
              <option value="FAMILY">경조사</option>
              <option value="SPECIAL">특별휴가</option>
            </select>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div><label className="t-label">시작일</label><input type="date" className="t-input" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })}/></div>
            <div><label className="t-label">종료일</label><input type="date" className="t-input" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })}/></div>
          </div>
          <div><label className="t-label">사유</label><textarea className="t-textarea" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}/></div>
        </div>
        <div style={{ padding: 16, borderTop: "1px solid var(--border)", display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="t-btn" onClick={onClose}>취소</button>
          <button className="t-btn t-btn-primary" onClick={submit} disabled={submitting}>{submitting ? "신청 중…" : "신청"}</button>
        </div>
      </div>
    </div>
  );
}
